// ─────────────────────────────────────────────────────────────────────────────
// SurvAIHealth Metrics — Grievance Log (F585) shared data model
//
// ONE shared record shape that all 9 grievance reports read. Fields the live
// data-relay feed may not carry are RESERVED and allowed to be null — reports
// render those as a muted "— not in feed" state and never break.
//
// This module also holds the derived-field helpers (daysToClose / isOverdue /
// notificationGap) so every report computes them identically.
// ─────────────────────────────────────────────────────────────────────────────

// Canonical record shape (documentation — JS has no static types here):
//
// {
//   id,                       // string
//   facilityCcn,              // e.g. "505289"
//   facilityName,             // e.g. "Birch Creek"
//   complainantType,          // "resident" | "family" | "representative" | "anonymous"
//   residentName,             // string | null
//   dateReceived,             // ISO date
//   category,                 // see CATEGORIES
//   department,               // see DEPARTMENTS
//   assignedTo,               // grievance official name
//   status,                   // "open" | "in_progress" | "resolved" | "closed"
//   investigationNotes,       // string
//   resolutionDate,           // ISO date | null
//   outcome,                  // string | null
//   residentNotificationSent, // boolean | null  <- RESERVED F585 gate field
//   residentNotificationDate, // ISO date | null  <- RESERVED
//   employeesMentioned,       // string[]
//   narrative,                // free-text body (powers keyword search + summaries)
// }

// ── The 6 pilot facilities (mock data is keyed by these CCNs) ────────────────
export const PILOT_FACILITIES = [
  { ccn: "505289", name: "Birch Creek" },
  { ccn: "515186", name: "Bluestone" },
  { ccn: "505387", name: "Woodard Creek" },
  { ccn: "295071", name: "TLC Care Center" },
  { ccn: "195221", name: "Ormond Nursing" },
  { ccn: "445457", name: "Monroe Health" },
  // Fully-populated demo facility (admin/VP) — a sample facility that always
  // renders sample data across every report section, without touching live data.
  { ccn: "999012", name: "St. Jude Skilled Nursing (Demo)", demo: true },
  // TWO SAMPLE FACILITIES UNDER ONE ORGANISATION.
  //
  // A single demo facility shows what a building looks like. It does not show
  // the thing a multi-site operator actually asks about, which is whether the
  // scope follows them — pick the org and see their buildings, pick a building
  // and see only that one. Two facilities sharing an organization_id is the
  // smallest arrangement that demonstrates it, and every report already filters
  // by facilityCcn, so the scoping is exercised rather than described.
  //
  // 999xxx deliberately: a real CCN is a six-digit CMS certification number and
  // none of them start 999, so a sample facility can never be mistaken for a
  // certified one, and a printed report carrying this CCN is self-evidently not
  // a submission.
  { ccn: "999101", name: "St. Margaret's Care Center", demo: true, organization_id: "org-st-margarets-health" },
  { ccn: "999102", name: "St. Mary's Skilled Nursing", demo: true, organization_id: "org-st-margarets-health" },
];

// Sample facilities never touch the live feed, whichever one is selected. Was a
// single-CCN equality check against St. Jude, which silently excluded any
// sample facility added after it.
export const DEMO_METRICS_CCNS = new Set(
  PILOT_FACILITIES.filter((f) => f.demo).map((f) => String(f.ccn)),
);
export const isDemoMetricsFacility = (ccn) => DEMO_METRICS_CCNS.has(String(ccn));

// The always-on sample facility for Metrics & Reporting. Selecting it forces the
// "mock" data source so every report section is populated (for demos / training),
// even on a PCC-enabled org where real facilities stay live.
export const DEMO_METRICS_CCN = "999012";
export const DEMO_METRICS_FACILITY = PILOT_FACILITIES.find((f) => f.ccn === DEMO_METRICS_CCN);

// ── Live PCC facilities (training/sandbox) ───────────────────────────────────
// Selectable so live data can be pulled once PCC is enabled. The `ccn` value is
// what gets passed to the proxy as the PCC `facId`, so it MUST be the real
// integer facId PCC assigns (shown in the PCC UI footer as "PCC Facility ID"),
// NOT the label number. The sandbox facility labeled "FACILITY_12" has real
// facId 6615 — sending "FACILITY_12" (or "12") returns a 400 / empty roster.
// `pcc: true` flags it as a live-feed facility in the UI. Add more here (or via
// window.PCC_FACILITIES) as real facilities are provisioned.
export const PCC_SANDBOX_FACILITIES = [
  { ccn: "6615", name: "PointClickCare Sandbox — FACILITY_12", pcc: true },
];

// The facility list the selector should show: pilots + any PCC facilities, plus
// optional runtime additions via window.PCC_FACILITIES = [{ccn,name,pcc:true}].
export function getMetricsFacilities() {
  const runtime = (typeof window !== "undefined" && Array.isArray(window.PCC_FACILITIES)) ? window.PCC_FACILITIES : [];
  return [...PILOT_FACILITIES, ...PCC_SANDBOX_FACILITIES, ...runtime];
}

export const METRICS_FACILITIES = getMetricsFacilities();

// ── Enumerations + display labels/colors (match app slate/blue palette) ───────
export const CATEGORIES = {
  care:          { label: "Care",            color: "var(--ed-brand)", bg: "var(--ed-brand-soft)" },
  staff_conduct: { label: "Staff Conduct",   color: "var(--ed-violet)", bg: "var(--ed-violet-soft)" },
  environment:   { label: "Environment",     color: "var(--ed-brand-lift)", bg: "#ecfeff" },
  dietary:       { label: "Dietary",         color: "var(--ed-success)", bg: "#ecfdf5" },
  billing:       { label: "Billing",         color: "#d97706", bg: "var(--ed-amber-soft)" },
  rights:        { label: "Rights",          color: "#be123c", bg: "#fff1f2" },
  other:         { label: "Other",           color: "var(--ed-muted)", bg: "var(--ed-line-2)" },
};

export const DEPARTMENTS = {
  nursing:         { label: "Nursing" },
  dietary:         { label: "Dietary" },
  housekeeping:    { label: "Housekeeping" },
  administration:  { label: "Administration" },
  therapy:         { label: "Therapy" },
  social_services: { label: "Social Services" },
};

export const STATUSES = {
  open:        { label: "Open",        color: "var(--ed-alert-deep)", bg: "var(--ed-alert-soft)", border: "var(--ed-alert-line)" },
  in_progress: { label: "In Progress", color: "var(--ed-amber-deep)", bg: "var(--ed-amber-soft)", border: "var(--ed-amber-line)" },
  resolved:    { label: "Resolved",    color: "var(--ed-success-deep)", bg: "var(--ed-success-soft)", border: "var(--ed-success-line)" },
  closed:      { label: "Closed",      color: "var(--ed-ink-2)", bg: "var(--ed-line-2)", border: "var(--ed-line)" },
};

export const COMPLAINANT_TYPES = {
  resident:       { label: "Resident" },
  family:         { label: "Family" },
  representative: { label: "Representative" },
  anonymous:      { label: "Anonymous" },
};

export const OPEN_STATUSES = ["open", "in_progress"];
export const CLOSED_STATUSES = ["resolved", "closed"];

// ── Label lookups (safe — unknown keys fall back to the raw value) ────────────
export const categoryLabel   = (k) => CATEGORIES[k]?.label   || k || "—";
export const departmentLabel = (k) => DEPARTMENTS[k]?.label   || k || "—";
export const statusLabel     = (k) => STATUSES[k]?.label      || k || "—";
export const complainantLabel = (k) => COMPLAINANT_TYPES[k]?.label || k || "—";

// ── Date utilities ────────────────────────────────────────────────────────────
export function parseDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function daysBetween(fromIso, toIso) {
  const a = parseDate(fromIso);
  const b = parseDate(toIso);
  if (!a || !b) return null;
  return Math.round((b.getTime() - a.getTime()) / MS_PER_DAY);
}

// ── Derived helpers — used identically across all 9 reports ───────────────────

// Integer days from received to resolution, or null if either date is missing.
export function daysToClose(record) {
  if (!record) return null;
  return daysBetween(record.dateReceived, record.resolutionDate);
}

// Open/in_progress and older than the SLA (default 5 days per F585 timeliness).
export function isOverdue(record, slaDays = 5, now = new Date()) {
  if (!record) return false;
  if (!OPEN_STATUSES.includes(record.status)) return false;
  const age = daysBetween(record.dateReceived, now.toISOString());
  return age != null && age > slaDays;
}

// Age in days of a still-open grievance (received -> now), null if no date.
export function openAgeDays(record, now = new Date()) {
  if (!record) return null;
  return daysBetween(record.dateReceived, now.toISOString());
}

// The headline F585 risk: resolved/closed but resident was NOT notified.
// residentNotificationSent === null (feed gap) counts as a gap — !== true.
export function notificationGap(record) {
  if (!record) return false;
  if (!CLOSED_STATUSES.includes(record.status)) return false;
  return record.residentNotificationSent !== true;
}

// ── Aggregations shared by several reports ────────────────────────────────────

// median of a numeric array (ignoring null/undefined); null when empty.
export function median(nums) {
  const xs = (nums || []).filter((n) => typeof n === "number" && !isNaN(n)).sort((a, b) => a - b);
  if (!xs.length) return null;
  const mid = Math.floor(xs.length / 2);
  return xs.length % 2 ? xs[mid] : (xs[mid - 1] + xs[mid]) / 2;
}

export function average(nums) {
  const xs = (nums || []).filter((n) => typeof n === "number" && !isNaN(n));
  if (!xs.length) return null;
  return xs.reduce((a, b) => a + b, 0) / xs.length;
}

// Count records grouped by a key (returns [{key, count}] sorted desc).
export function countBy(records, keyFn) {
  const map = {};
  (records || []).forEach((r) => {
    const k = keyFn(r);
    if (k == null) return;
    map[k] = (map[k] || 0) + 1;
  });
  return Object.entries(map)
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count);
}
